import { useEffect, useState } from "react";
import { api, idr } from "../lib/api";
import { Printer, Save, ReceiptText, Layers } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import InputRupiah from "../components/InputRupiah";
import { MonthPicker } from "@/components/ui/date-picker";
import { bulanIniISO } from "@/lib/tanggal";

export default function Payroll() {
  const { user } = useAuth();
  const canSave = user?.role !== "viewer";
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState("");
  const [form, setForm] = useState({
    periode: bulanIniISO(),
    insentif_target: 0, bonus_kpi: 0, bonus_performa: 0,
    lembur_jam: 0, menit_telat: 0, hari_alpha: 0, menit_pulang_awal: 0,
    kasbon: 0, potongan_lain: 0,
  });
  const [preview, setPreview] = useState(null);
  const [useAttendance, setUseAttendance] = useState(false);
  const [useFromPoints, setUseFromPoints] = useState(false);

  useEffect(() => { api.get("/employees").then(r => setEmployees(r.data)); }, []);

  const doPreview = async () => {
    if (!selected) { toast.error("Pilih karyawan"); return; }
    try {
      const { data } = await api.post("/payroll/preview", { employee_id: selected, ...form, auto_from_attendance: useAttendance, insentif_from_points: useFromPoints });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const doSave = async () => {
    try {
      await api.post("/payroll/save", { employee_id: selected, ...form, auto_from_attendance: useAttendance, insentif_from_points: useFromPoints });
      toast.success("Slip gaji tersimpan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const upd = (k, v) => setForm(s => ({ ...s, [k]: typeof v === "number" ? v : parseFloat(v) || 0 }));

  const doBulk = async () => {
    if (!confirm(`Buat slip default untuk semua karyawan aktif periode ${form.periode}? Karyawan yang sudah punya slip di periode ini akan di-skip.`)) return;
    try {
      const { data } = await api.post("/payroll/bulk", { periode: form.periode });
      toast.success(`Bulk selesai: ${data.created} slip dibuat, ${data.skipped} di-skip dari ${data.total} karyawan`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  // Rincian insentif berjenjang (brief bos #26) — hanya terisi kalau slip
  // dihitung dengan "insentif otomatis dari poin" aktif (routers/payroll.py
  // _terapkan_tier_insentif). Slip lama/manual tidak punya field ini, jadi
  // tampilan lama (Row flat) tetap dipertahankan lewat pengecekan `ps?.insentif_cair`.
  const ps = preview?.calc?.points_summary || null;

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="mb-8 no-print">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Payroll</p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Generator Slip Gaji</h1>
        <p className="mt-2 text-sm text-slate-500">Pilih karyawan lalu isi variabel gaji bulanan untuk menghasilkan slip.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm no-print">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Input Data</h3>
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Pilih Karyawan *</span>
              <select value={selected} onChange={e => setSelected(e.target.value)} data-testid="payroll-select-employee"
                className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
                <option value="">— pilih karyawan —</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.nama} — {e.grade_jabatan}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Periode</span>
              <MonthPicker value={form.periode} onChange={v => setForm(s => ({ ...s, periode: v }))}
                className="mt-1" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["insentif_target","Insentif Target"],["bonus_kpi","Bonus KPI"],
                ["bonus_performa","Bonus Performa"],["lembur_jam","Lembur (jam)"],
                ["menit_telat","Menit Telat"],["hari_alpha","Hari Alpha"],
                ["menit_pulang_awal","Menit Pulang Awal"],
                ["kasbon","Kasbon"],["potongan_lain","Potongan Lain"],
              ].map(([k, l]) => {
                const isUang = ["insentif_target", "bonus_kpi", "bonus_performa", "kasbon", "potongan_lain"].includes(k);
                return (
                  <label key={k} className="block">
                    <span className="text-xs font-medium text-slate-600">{l}</span>
                    {isUang ? (
                      <InputRupiah value={form[k]} onChange={n => upd(k, n)} testid={`payroll-${k}`}
                        className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                    ) : (
                      <input type="number" value={form[k]} onChange={e => upd(k, e.target.value)} data-testid={`payroll-${k}`}
                        className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                    )}
                  </label>
                );
              })}
            </div>

            {/* Auto toggles */}
            <div className="border border-emerald-100 bg-emerald-50/40 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Otomasi Perhitungan</p>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={useAttendance} onChange={e => setUseAttendance(e.target.checked)}
                  data-testid="payroll-auto-attendance" className="w-4 h-4 accent-emerald-600" />
                <span className="text-slate-700">Auto dari absensi <span className="text-slate-500 text-xs">(hitung menit telat & hari alpha dari data attendance)</span></span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={useFromPoints} onChange={e => setUseFromPoints(e.target.checked)}
                  data-testid="payroll-from-points" className="w-4 h-4 accent-sky-600" />
                <span className="text-slate-700">Insentif otomatis dari poin sales <span className="text-slate-500 text-xs">(total poin × bobot per poin dari data karyawan)</span></span>
              </label>
            </div>

            <div className="flex gap-2 pt-2">
              <button onClick={doPreview} data-testid="btn-preview-payroll"
                className="flex-1 h-10 rounded-lg bg-[#0F172A] hover:bg-slate-800 text-white text-sm font-medium transition-colors">
                Hitung Preview
              </button>
              {canSave && (
                <button onClick={doSave} disabled={!preview} data-testid="btn-save-payroll"
                  className="h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
                  <Save className="w-4 h-4" /> Simpan
                </button>
              )}
            </div>

            {/* Generate massal: fungsinya (doBulk) dan endpointnya sudah ada sejak
                awal, tapi tidak pernah tersambung ke elemen mana pun — ikon Layers
                bahkan sudah diimpor. Akibatnya fitur ini tidak bisa dipakai siapa
                pun. Dipisah dari baris tombol di atas karena cakupannya beda:
                yang di atas satu karyawan, yang ini SEMUA karyawan aktif. */}
            {canSave && (
              <div className="border-t border-slate-200 pt-3">
                <button onClick={doBulk} data-testid="btn-bulk-payroll"
                  className="w-full h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium inline-flex items-center justify-center gap-2">
                  <Layers className="w-4 h-4" /> Generate Massal — semua karyawan aktif
                </button>
                <p className="mt-1.5 text-xs text-slate-500">
                  Membuat slip default periode {form.periode} untuk semua karyawan aktif.
                  Yang sudah punya slip di periode ini dilewati, tidak ditimpa.
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border-2 border-slate-200 rounded-xl p-8 print-area shadow-sm min-h-[400px]">
          {!preview ? (
            <div className="h-full grid place-items-center text-center py-16">
              <div>
                <ReceiptText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Preview slip gaji akan tampil di sini</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between border-b-2 border-slate-900 pb-4 mb-6">
                <div>
                  <p className="font-display font-bold text-lg text-slate-900">SLIP GAJI</p>
                  <p className="text-xs text-slate-500">Periode {preview.periode}</p>
                </div>
                <div className="text-right">
                  <p className="font-display font-bold text-slate-900">ZOA Group</p>
                  <p className="text-xs text-slate-500">HRIS Payroll</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                <div>
                  <p className="text-xs text-slate-500">Nama</p>
                  <p className="font-semibold text-slate-900">{preview.employee.nama}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">NIK</p>
                  <p className="font-mono text-slate-900">{preview.employee.nik}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Grade</p>
                  <p className="text-slate-900">{preview.employee.grade_jabatan}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Branch</p>
                  <p className="text-slate-900">{preview.employee.branch}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-8">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">Pendapatan</p>
                  <Row l="Gaji Pokok" v={preview.calc.gaji_pokok} />
                  <Row l="Tunj. Jabatan" v={preview.calc.tunj_jabatan} />
                  <Row l="Tunj. Makan" v={preview.calc.tunj_makan} />
                  <Row l="Tunj. Istri/Suami" v={preview.calc.tunj_istri} />
                  <Row l="Tunj. Anak" v={preview.calc.tunj_anak} />
                  <Row l="Tunj. Lainnya" v={preview.calc.tunj_lainnya} />
                  {ps?.insentif_cair !== undefined ? (
                    <div className="py-1.5 text-sm border-b border-slate-100" data-testid="payroll-insentif-tier">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-700">Insentif Target</span>
                        <span className="font-mono text-slate-900">{idr(preview.calc.insentif_target)}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Realisasi {ps.pct_realisasi}% → tier cair {ps.tier_cair_pct}% → insentif {idr(ps.insentif_cair)} dari dasar {idr(ps.insentif_dasar)}
                      </p>
                    </div>
                  ) : (
                    <Row l="Insentif Target" v={preview.calc.insentif_target} />
                  )}
                  <Row l="Bonus KPI" v={preview.calc.bonus_kpi} />
                  <Row l="Bonus Performa" v={preview.calc.bonus_performa} />
                  <Row l={`Lembur (${preview.calc.lembur_jam} jam)`} v={preview.calc.lembur} />
                  <Row l="Total Bruto" v={preview.calc.total_bruto} bold />
                  {ps && (
                    <div className="mt-3 pt-3 border-t border-slate-100 text-xs bg-sky-50 -mx-2 p-2 rounded">
                      <p className="font-semibold text-sky-900 mb-1">Detail Poin Sales</p>
                      <p className="text-slate-700">HP: {ps.breakdown.sale_hp.n} × {ps.breakdown.sale_hp.rule} = {ps.breakdown.sale_hp.poin} poin</p>
                      <p className="text-slate-700">ACC: {ps.breakdown.sale_acc.n} × {ps.breakdown.sale_acc.rule} = {ps.breakdown.sale_acc.poin} poin</p>
                      <p className="text-slate-700">Akuisisi HP: {ps.breakdown.acq_hp.n} × {ps.breakdown.acq_hp.rule} = {ps.breakdown.acq_hp.poin} poin</p>
                      <p className="text-slate-700">Akuisisi Device: {ps.breakdown.acq_device.n} × {ps.breakdown.acq_device.rule} = {ps.breakdown.acq_device.poin} poin</p>
                      {ps.breakdown.per_gudang && Object.keys(ps.breakdown.per_gudang).length > 0 && (
                        <div className="mt-1.5 pt-1.5 border-t border-sky-100" data-testid="payroll-per-gudang">
                          <p className="text-slate-600 font-medium">Rincian per Gudang</p>
                          {Object.values(ps.breakdown.per_gudang).map((g, i) => (
                            <p key={i} className="text-slate-700">{g.warehouse_nama}: {g.n} unit = {g.poin} poin</p>
                          ))}
                        </div>
                      )}
                      <p className="mt-1 font-semibold text-sky-900">Total: {ps.total_poin} poin × {idr(ps.bobot_per_poin)} = {idr(ps.insentif_rupiah)}</p>
                      {ps.target_poin > 0 && (
                        <p className="text-sky-700">Pencapaian: {ps.total_poin} / {ps.target_poin} ({ps.realisasi_pct}%)</p>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-rose-700 mb-2">Potongan</p>
                  <Row l={`Denda Telat (${preview.calc.menit_telat} mnt)`} v={preview.calc.denda_telat} />
                  <Row l={`Denda Alpha (${preview.calc.hari_alpha} hari)`} v={preview.calc.denda_alpha} />
                  {/* Potongan tunjangan makan otomatis (brief bos #14) — komponen TERPISAH
                      dari Denda Alpha di atas, sama-sama dipicu hari_alpha tapi rumus beda:
                      hari_alpha × (tunj_makan / pembagi). `?? 0` untuk slip lama yang belum
                      punya field ini di dokumennya. */}
                  <Row l={`Pot. Tunj. Makan Alpa (${preview.calc.hari_alpha} hari ÷ ${preview.calc.pembagi_tunj_makan_alpa ?? 26})`}
                    v={preview.calc.potongan_tunj_makan_alpa ?? 0} />
                  {(preview.calc.denda_pulang_awal > 0 || preview.calc.menit_pulang_awal > 0) && (
                    <Row
                      l={`Denda Pulang Awal (${preview.calc.menit_pulang_awal} mnt @ ${idr(preview.calc.tarif_pulang_awal_per_jam)}/jam${preview.calc.tarif_pulang_awal_sumber === "karyawan" ? " · khusus" : ""})`}
                      v={preview.calc.denda_pulang_awal} />
                  )}
                  {preview.calc.hari_kerja_kurang > 0 && (
                    <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 my-1"
                      data-testid="payroll-short-shift-note">
                      {preview.calc.hari_kerja_kurang} hari jam kerjanya di bawah ambang &rarr; dihitung tidak hadir (masuk Denda Alpha).
                    </p>
                  )}
                  <Row l="Kasbon" v={preview.calc.kasbon} />
                  <Row l="Potongan Lain" v={preview.calc.potongan_lain} />
                  <Row l="Total Potongan" v={preview.calc.total_potongan} bold />
                </div>
              </div>

              <div className="mt-6 pt-4 border-t-2 border-slate-900 flex items-center justify-between">
                <p className="font-display font-bold text-lg text-slate-900">Take Home Pay</p>
                <p className="font-display font-bold text-2xl text-emerald-700" data-testid="payroll-thp">{idr(preview.calc.thp)}</p>
              </div>

              <div className="mt-6 flex justify-end no-print">
                <button onClick={() => window.print()}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-sm hover:bg-slate-50">
                  <Printer className="w-4 h-4" /> Cetak
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ l, v, bold }) {
  return (
    <div className={`flex items-center justify-between py-1.5 text-sm border-b border-slate-100 last:border-0 ${bold ? "font-bold text-slate-900" : "text-slate-700"}`}>
      <span>{l}</span>
      <span className="font-mono">{idr(v)}</span>
    </div>
  );
}
